/* The design programme's key dates.
   Once the brief is approved there's a real target to plan against, so these
   milestones get pushed onto the Task Mind Map — that's where the studio sets
   the dates — and the dates come back to fill this project's calendar.
   Pure logic: no React, no store. */

import type { Project, ScheduleEvent, StageKey } from "./types";

export interface MilestoneSeed {
  key: string;
  title: string;
  stage: StageKey;
  kind: ScheduleEvent["kind"];
  /** Roughly how far into the programme this usually falls — a starting offer,
   *  in weeks from the brief being approved. The studio sets the real date. */
  weeks: number;
  note: string;
}

/** The spine of a design programme, from an approved brief through to tender. */
export const BRIEF_MILESTONES: MilestoneSeed[] = [
  { key: "kickoff",   title: "Design kick-off",              stage: "concept",       kind: "meeting",   weeks: 1,  note: "Walk the approved brief through with the design team." },
  { key: "concept",   title: "Concept design presented",     stage: "concept",       kind: "milestone", weeks: 4,  note: "Direction, massing and spatial planning shown to the client." },
  { key: "conceptok", title: "Concept signed off",           stage: "concept",       kind: "milestone", weeks: 6,  note: "The client's decision to proceed on the concept." },
  { key: "developed", title: "Developed design complete",    stage: "developed",     kind: "milestone", weeks: 12, note: "Every space resolved, materials fixed, services coordinated." },
  { key: "present",   title: "Design presentation",          stage: "presentation",  kind: "meeting",   weeks: 13, note: "The resolved scheme walked through space by space." },
  { key: "approved",  title: "Design approved",              stage: "presentation",  kind: "milestone", weeks: 15, note: "Sign-off to start technical documentation." },
  { key: "ift",       title: "IFT set issued",               stage: "documentation", kind: "milestone", weeks: 24, note: "The full technical set, issued for tender." },
  { key: "tender",    title: "Tender issued",                stage: "delivery",      kind: "milestone", weeks: 25, note: "Documents to market." },
  { key: "award",     title: "Contractor appointed",         stage: "delivery",      kind: "milestone", weeks: 31, note: "Award and contract signed." },
];

/** Milestones only make sense once the brief is agreed — before that the target moves. */
export function milestonesReady(p: Project): boolean {
  return p.brief?.status === "approved";
}

function addWeeks(iso: string, w: number): string {
  const d = new Date(iso);
  if (isNaN(+d)) return "";
  d.setDate(d.getDate() + w * 7);
  return d.toISOString().slice(0, 10);
}

/** The anchor the programme counts from: when the brief was approved. */
export function briefAnchor(p: Project, today: string): string {
  return p.brief?.approvedOn || p.brief?.updatedOn || today;
}

/** What we'd propose, before the studio sets the real dates on the mind map.
 *  Anything already on the calendar keeps its date. */
export function proposedMilestones(p: Project, today: string): (MilestoneSeed & { date: string })[] {
  const anchor = briefAnchor(p, today);
  return BRIEF_MILESTONES.map((m) => {
    const existing = (p.schedule ?? []).find((e) => e.title === m.title);
    return { ...m, date: existing?.date || addWeeks(anchor, m.weeks) };
  });
}

/** Fold dates coming back from the mind map into the calendar: update the event
 *  we already made for a milestone, or add it. Events the studio created by hand
 *  are left alone. Returns the new schedule and what changed. */
export function applyMilestoneDates(
  schedule: ScheduleEvent[],
  dated: { key: string; title: string; date: string; kind: ScheduleEvent["kind"]; stage: StageKey }[],
  newId: () => string,
): { schedule: ScheduleEvent[]; added: number; moved: string[] } {
  const next = [...schedule];
  let added = 0;
  const moved: string[] = [];
  for (const d of dated) {
    if (!d.date) continue;
    const i = next.findIndex((e) => e.title === d.title);
    if (i === -1) {
      next.push({ id: newId(), title: d.title, date: d.date, kind: d.kind, stage: d.stage });
      added++;
    } else if (next[i].date !== d.date) {
      moved.push(d.title);
      next[i] = { ...next[i], date: d.date };
    }
  }
  next.sort((a, b) => +new Date(a.date) - +new Date(b.date));
  return { schedule: next, added, moved };
}
